import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import mailIcon from "../../assets/imgs/contact-us/email.svg";
import whatsappIcon from "../../assets/imgs/contact-us/whatsapp.svg";
import { AgentText, shiftText } from "../../data/texts";
import { Owner } from "../../types/owner";
import useScreen from "../common/UseScreen";
import { AvailableHours } from "./AvailableHours";
import { useEffect, useState } from "react";
import { GenericErrors } from "../../types/enums";

interface AgentDetails {
  agent?: Owner;
  isLoading: boolean;
}
export const AgentDetails = ({ agent, isLoading }: AgentDetails) => {
  const { isMobile } = useScreen();
  const theme = useTheme();
  const [availablityMessage, setAvailablityMessage] = useState({
    isAvailable: false,
    msg: "",
  });
  const [shiftHours, setShiftHours] = useState({
    morning: { start: 0, end: 0 },
    night: { start: 0, end: 0 },
  });

  const initiateAvailability = () => {
    if (!agent?.shifts) {
      return;
    }
    const israelMorningEndingTime = DateTime.fromObject(
      { hour: 15, minute: 0 },
      { zone: "Asia/Jerusalem" }
    );

    const israelNightEndingTime = DateTime.fromObject(
      { hour: 17, minute: 0 },
      { zone: "Asia/Jerusalem" }
    );

    const israelMorningStartTime = DateTime.fromObject(
      { hour: 7, minute: 0 },
      { zone: "Asia/Jerusalem" }
    );
    const israelNightStartTime = DateTime.fromObject(
      { hour: 2, minute: 0 },
      { zone: "Asia/Jerusalem" }
    );

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userMorningEndingTime = israelMorningEndingTime.setZone(userTimezone);
    const userNightEndingTime = israelNightEndingTime.setZone(userTimezone);
    const userMorningStartTime = israelMorningStartTime.setZone(userTimezone);
    const userNightStartTime = israelNightStartTime.setZone(userTimezone);
    const userHour = new Date().getHours();

    setShiftHours({
      morning: {
        start: userMorningStartTime.hour,
        end: userMorningEndingTime.hour,
      },
      night: {
        start: userNightStartTime.hour,
        end: userNightEndingTime.hour,
      },
    });

    const todayShift = agent?.shifts;

    switch (todayShift) {
      case "morning":
        {
          if (userHour >= userMorningEndingTime.hour) {
            setAvailablityMessage({
              isAvailable: false,
              msg: shiftText.notAvailable,
            });
          } else {
            setAvailablityMessage({
              isAvailable: true,
              msg: shiftText.morningAvailability,
            });
          }
        }
        break;

      case "night":
        {
          if (userHour >= userNightEndingTime.hour) {
            setAvailablityMessage({
              isAvailable: false,
              msg: shiftText.notAvailable,
            });
          } else {
            setAvailablityMessage({
              isAvailable: true,
              msg: shiftText.nightAvailability,
            });
          }
        }
        break;

      case "dayoff":
        return shiftText.dayoff;
    }
  };

  useEffect(() => {
    // this causing a bug
    initiateAvailability();
  }, [agent]);

  if (!agent && !isLoading) {
    return;
  }

  const onButtonClick = (cta: "whatsapp" | "email") => {
    switch (cta) {
      case "whatsapp":
        if (agent?.phone) {
          console.log(isMobile);

          if (isMobile) {
            window.open(`whatsapp://send?phone=${agent.phone}`);
          } else {
            window.open(`https://wa.me/${agent.phone}`);
          }
        }
        break;
      case "email":
        if (agent?.email) {
          window.open(`mailto:${agent.email}`);
        }
        break;
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          marginBlock: "20px",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <Stack
      gap={"20px"}
      sx={{
        width: isMobile ? "unset" : "100%",
        marginInline: isMobile ? "24px" : "0",
        marginBottom: "20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 6px 0px #4C4E641A",
        background: "white",
        padding: "20px",
      }}
      justifyContent={"space-between"}
      flexWrap={isMobile ? "wrap" : "nowrap"}
    >
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        gap={isMobile ? "16px" : "0"}
        width={"100%"}
      >
        <Stack
          flexGrow={"1"}
          direction={"row"}
          alignItems={"center"}
          gap={"16px"}
        >
          {/* Avatar */}
          <Box sx={{ position: "relative", minWidth: "110px" }}>
            <Box
              sx={{
                width: "23.5px",
                height: "23.5px",
                border: "2.5px solid white",
                background: availablityMessage.isAvailable ? "#1DBF73" : "red",
                borderRadius: "50%",
                position: "absolute",
                top: "70%",
                right: "0",
              }}
            ></Box>
            <img
              src={agent?.avatar}
              alt="Avatar"
              style={{
                borderRadius: "50%",
                objectFit: "cover",
                height: "100%",
                minWidth: "100px",
              }}
            />
          </Box>
          {/* AGENT NAME */}
          <Stack gap={"6px"}>
            <Typography color="#0250A5" fontWeight={"500"} fontSize={"20px"}>
              {agent?.name}
            </Typography>

            <Box
              sx={{
                background: theme.palette.text.primary,
                color: "white",
                paddingBlock: "6px",
                paddingInline: "10px",
                borderRadius: "111px",
                width: "fit-content",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              {AgentText.PROFESSIONAL_AGENT}
            </Box>
            <Typography fontWeight={"400"} color="#797979">
              {agent?.email}
            </Typography>
            <Typography fontWeight={"400"} color={theme.palette.text.primary}>
              {availablityMessage.msg}
            </Typography>
          </Stack>
        </Stack>

        {/* CONTACT BUTTONS */}
        <Stack gap={"16px"}>
          <Button
            onClick={() => onButtonClick("whatsapp")}
            sx={{
              background: theme.palette.text.primary,
              borderRadius: "8px",
              display: "flex",
              gap: "6px",
            }}
            variant="contained"
          >
            <img src={whatsappIcon} />
            {AgentText.CONTACT_ME}
          </Button>

          <Button
            onClick={() => onButtonClick("email")}
            variant="outlined"
            sx={{
              display: "flex",
              borderRadius: "8px",
              gap: "6px",
              color: theme.palette.text.primary,
            }}
          >
            <img src={mailIcon} />
            {AgentText.SEND_EMAIL}
          </Button>
        </Stack>
      </Stack>
      <Divider />
      {agent?.shifts ? (
        <AvailableHours
          morningHours={shiftHours.morning}
          nightHours={shiftHours.night}
          shifts={agent?.shifts}
        />
      ) : (
        <Typography>{GenericErrors.UNAVAILABLE_INFORMATION}</Typography>
      )}

      {/* <ContactBox
      sx={{ width: "50%" }}
      iconSrc={ImageService.getLocalIconPath(
        Icons.Folders.CONTACT_US,
        Icons.CONTACT_US.PHONE
      )}
      title={agent?.phone || ContactUsText.PHONE_NUMBER}
      subtitle={ContactUsText.HAPPY_HELP}
    />

    <ContactBox
      sx={{ width: "50%" }}
      iconSrc={ImageService.getLocalIconPath(
        Icons.Folders.CONTACT_US,
        Icons.CONTACT_US.EMAIL
      )}
      subtitle={ContactUsText.BEST_WAY}
      title={agent?.email || ContactUsText.EMAIL}
    /> */}
    </Stack>
  );
};
